import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { statsApi } from "../lib/api";
import KPICard from "../components/KPICard";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../components/ui/popover";
import { toast } from "sonner";
import { Loader2, Calendar, TrendingUp, TrendingDown, Target, Clock, DollarSign, FileText, MessageSquare, CalendarRange, Users } from "lucide-react";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";

const MONTHS = [
  { value: 0, label: "All Year (YTD)" },
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

const EmployeeDashboard = () => {
  const { user, tracksStats, tracksBillings } = useAuth();
  const [loading, setLoading] = useState(true);
  const [ytdData, setYtdData] = useState(null);
  const [monthlyStats, setMonthlyStats] = useState([]);
  const [selectedYear, setSelectedYear] = useState(null);
  const [selectedMonth, setSelectedMonth] = useState(0);
  const [availableYears, setAvailableYears] = useState([new Date().getFullYear()]);
  const [useCustomRange, setUseCustomRange] = useState(false);
  const [customRange, setCustomRange] = useState({
    startMonth: 1,
    startYear: new Date().getFullYear(),
    endMonth: new Date().getMonth() + 1,
    endYear: new Date().getFullYear(),
  });
  const [customRangeData, setCustomRangeData] = useState(null);
  
  // Partners (tracksBillings) should also see their personal stats
  const shouldShowStats = tracksStats || tracksBillings;

  useEffect(() => {
    if (user?.id) {
      loadDashboardData();
    }
  }, [user?.id]);

  useEffect(() => {
    if (user?.id && selectedYear !== null) {
      loadDashboardData(selectedYear, selectedMonth || null);
    }
  }, [selectedYear, selectedMonth]);

  const loadDashboardData = async (year = null, month = null) => {
    // If user doesn't track stats and isn't a partner, skip loading stats
    if (!shouldShowStats) {
      setLoading(false);
      return;
    }
    
    try {
      const [ytdRes, statsRes] = await Promise.all([
        statsApi.getYtd(user.id, year, month),
        statsApi.getByUser(user.id),
      ]);

      setYtdData(ytdRes.data);
      setAvailableYears(ytdRes.data.available_years || [new Date().getFullYear()]);
      if (selectedYear === null) {
        setSelectedYear(ytdRes.data.selected_year);
      }

      // Process monthly stats for chart
      const displayYear = year || ytdRes.data.selected_year || new Date().getFullYear();
      const currentMonth = new Date().getMonth() + 1; // 1-12
      const monthlyData = statsRes.data
        .filter((s) => {
          // Only include completed months for current year
          if (s.year === displayYear && displayYear === new Date().getFullYear()) {
            return s.month < currentMonth;
          }
          return s.year === displayYear;
        })
        .sort((a, b) => a.month - b.month)
        .map((s) => {
          // Billable % = Billable Hours / (Total Hours - PLT Hours)
          const workingHours = s.total_hours - (s.plt_hours || 0);
          const billablePercent =
            workingHours > 0 ? (s.billable_hours / workingHours) * 100 : 0;
          const realizationPercent =
            s.billed_amount > 0 ? (s.realized_amount / s.billed_amount) * 100 : 0;
          // Bill to Pay % = Realized (Billed) / Total Pay
          const billToPayPercent =
            s.total_pay > 0 ? (s.realized_amount / s.total_pay) * 100 : 0;

          const monthNames = [
            "Jan", "Feb", "Mar", "Apr", "May", "Jun",
            "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
          ];

          return {
            month: monthNames[s.month - 1],
            "Billable %": parseFloat(billablePercent.toFixed(1)),
            "Realization %": parseFloat(realizationPercent.toFixed(1)),
            "Bill to Pay %": parseFloat((billToPayPercent / 10).toFixed(1)),
          };
        });

      setMonthlyStats(monthlyData);
    } catch (error) {
      toast.error("Failed to load dashboard data");
    } finally {
      setLoading(false);
    }
  };

  const calculateCustomRangeData = async () => {
    if (!user?.id) return;
    
    try {
      const statsRes = await statsApi.getByUser(user.id);
      const allStats = statsRes.data;
      
      // Filter stats within the custom range
      const filteredStats = allStats.filter((s) => {
        const statDate = s.year * 12 + s.month;
        const startDate = customRange.startYear * 12 + customRange.startMonth;
        const endDate = customRange.endYear * 12 + customRange.endMonth;
        return statDate >= startDate && statDate <= endDate;
      });

      if (filteredStats.length === 0) {
        toast.error("No data found for the selected range");
        setCustomRangeData(null);
        return;
      }

      // Calculate aggregated stats
      const totals = filteredStats.reduce((acc, s) => ({
        billable_hours: acc.billable_hours + (s.billable_hours || 0),
        total_hours: acc.total_hours + (s.total_hours || 0),
        plt_hours: acc.plt_hours + (s.plt_hours || 0),
        billed_amount: acc.billed_amount + (s.billed_amount || 0),
        realized_amount: acc.realized_amount + (s.realized_amount || 0),
        total_pay: acc.total_pay + (s.total_pay || 0),
      }), { billable_hours: 0, total_hours: 0, plt_hours: 0, billed_amount: 0, realized_amount: 0, total_pay: 0 });

      const workingHours = totals.total_hours - totals.plt_hours;
      const billablePercent = workingHours > 0 ? (totals.billable_hours / workingHours) * 100 : 0;
      const realizationPercent = totals.billed_amount > 0 ? (totals.realized_amount / totals.billed_amount) * 100 : 0;
      const billToPayPercent = totals.total_pay > 0 ? (totals.realized_amount / totals.total_pay) * 100 : 0;

      setCustomRangeData({
        billable_percent: billablePercent.toFixed(1),
        realization_percent: realizationPercent.toFixed(1),
        bill_to_pay_percent: billToPayPercent.toFixed(1),
        months_count: filteredStats.length,
        ...totals,
      });
      
      toast.success(`Calculated stats for ${filteredStats.length} month(s)`);
    } catch (error) {
      toast.error("Failed to calculate custom range data");
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-spinner">
        <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
      </div>
    );
  }

  // Non-stats employee view (Front Office, Clerical) - but NOT partners
  if (!shouldShowStats) {
    return (
      <div className="space-y-8 animate-fade-in" data-testid="employee-dashboard">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Welcome, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-muted-foreground mt-1">
            {user?.position || "Team Member"}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Card className="hover:shadow-md transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-blue-500/20 flex items-center justify-center">
                  <FileText className="text-blue-500" size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Documents</h3>
                  <p className="text-sm text-muted-foreground">Access firm documents and your personal files</p>
                </div>
              </div>
              <Link to="/documents">
                <Button className="w-full bg-blue-600 hover:bg-blue-700" data-testid="go-to-documents">
                  View Documents
                </Button>
              </Link>
            </CardContent>
          </Card>

          <Card className="hover:shadow-md transition-shadow">
            <CardContent className="pt-6">
              <div className="flex items-center gap-4 mb-4">
                <div className="w-12 h-12 rounded-xl bg-emerald-500/20 flex items-center justify-center">
                  <MessageSquare className="text-emerald-500" size={24} />
                </div>
                <div>
                  <h3 className="font-semibold text-foreground">Feedback</h3>
                  <p className="text-sm text-muted-foreground">Give and receive feedback from your team</p>
                </div>
              </div>
              <Link to="/feedback">
                <Button className="w-full bg-emerald-600 hover:bg-emerald-700" data-testid="go-to-feedback">
                  View Feedback
                </Button>
              </Link>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const currentYear = ytdData?.current_year || {};
  const previousYear = ytdData?.previous_year || {};
  const benchmarks = ytdData?.benchmarks || {};
  const firmAverages = ytdData?.firm_averages || {};

  const getPeriodLabel = () => {
    if (selectedMonth > 0) {
      return `${MONTHS[selectedMonth].label} ${selectedYear}`;
    }
    if (ytdData?.period_label === "ytd") {
      return `Year-to-date ${selectedYear}`;
    }
    return `Full Year ${selectedYear}`;
  };

  return (
    <div className="space-y-8 animate-fade-in" data-testid="employee-dashboard">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-foreground">
            Welcome, {user?.name?.split(" ")[0]}
          </h1>
          <p className="text-muted-foreground mt-1">
            {getPeriodLabel()} vs {ytdData?.previous_year_value}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!useCustomRange ? (
            <>
              <Calendar size={18} className="text-muted-foreground" />
              <Select
                value={selectedYear?.toString()}
                onValueChange={(value) => setSelectedYear(parseInt(value))}
              >
                <SelectTrigger className="w-28" data-testid="year-selector">
                  <SelectValue placeholder="Year" />
                </SelectTrigger>
                <SelectContent>
                  {availableYears.map((year) => (
                    <SelectItem key={year} value={year.toString()}>
                      {year}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select
                value={selectedMonth.toString()}
                onValueChange={(value) => setSelectedMonth(parseInt(value))}
              >
                <SelectTrigger className="w-40" data-testid="month-selector">
                  <SelectValue placeholder="Month" />
                </SelectTrigger>
                <SelectContent>
                  {MONTHS.map((month) => (
                    <SelectItem key={month.value} value={month.value.toString()}>
                      {month.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </>
          ) : (
            <div className="flex items-center gap-2 p-2 bg-blue-500/10 rounded-lg">
              <CalendarRange size={18} className="text-blue-500" />
              <span className="text-sm font-medium text-blue-700">
                {MONTHS.find(m => m.value === customRange.startMonth)?.label} {customRange.startYear} - {MONTHS.find(m => m.value === customRange.endMonth)?.label} {customRange.endYear}
              </span>
            </div>
          )}
          
          <Popover>
            <PopoverTrigger asChild>
              <Button 
                variant={useCustomRange ? "default" : "outline"} 
                size="sm"
                className={useCustomRange ? "bg-blue-600 hover:bg-blue-700" : ""}
                data-testid="custom-range-btn"
              >
                <CalendarRange size={16} className="mr-1" />
                {useCustomRange ? "Edit Range" : "Custom Range"}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-80" align="end">
              <div className="space-y-4">
                <h4 className="font-semibold text-foreground">Select Date Range</h4>
                <p className="text-xs text-muted-foreground">Choose a custom range of months to aggregate your statistics.</p>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Start Month</Label>
                    <Select
                      value={customRange.startMonth.toString()}
                      onValueChange={(value) => setCustomRange(prev => ({ ...prev, startMonth: parseInt(value) }))}
                    >
                      <SelectTrigger className="h-9" data-testid="custom-start-month">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTHS.slice(1).map((month) => (
                          <SelectItem key={month.value} value={month.value.toString()}>
                            {month.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">Start Year</Label>
                    <Select
                      value={customRange.startYear.toString()}
                      onValueChange={(value) => setCustomRange(prev => ({ ...prev, startYear: parseInt(value) }))}
                    >
                      <SelectTrigger className="h-9" data-testid="custom-start-year">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[...Array(5)].map((_, i) => {
                          const year = new Date().getFullYear() - 3 + i;
                          return (
                            <SelectItem key={year} value={year.toString()}>
                              {year}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">End Month</Label>
                    <Select
                      value={customRange.endMonth.toString()}
                      onValueChange={(value) => setCustomRange(prev => ({ ...prev, endMonth: parseInt(value) }))}
                    >
                      <SelectTrigger className="h-9" data-testid="custom-end-month">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {MONTHS.slice(1).map((month) => (
                          <SelectItem key={month.value} value={month.value.toString()}>
                            {month.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs text-muted-foreground">End Year</Label>
                    <Select
                      value={customRange.endYear.toString()}
                      onValueChange={(value) => setCustomRange(prev => ({ ...prev, endYear: parseInt(value) }))}
                    >
                      <SelectTrigger className="h-9" data-testid="custom-end-year">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {[...Array(5)].map((_, i) => {
                          const year = new Date().getFullYear() - 3 + i;
                          return (
                            <SelectItem key={year} value={year.toString()}>
                              {year}
                            </SelectItem>
                          );
                        })}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="flex gap-2 pt-2">
                  <Button 
                    className="flex-1 bg-blue-600 hover:bg-blue-700" 
                    size="sm"
                    onClick={() => {
                      setUseCustomRange(true);
                      calculateCustomRangeData();
                    }}
                    data-testid="apply-custom-range"
                  >
                    Apply Range
                  </Button>
                  {useCustomRange && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => {
                        setUseCustomRange(false);
                        setCustomRangeData(null);
                      }}
                      data-testid="clear-custom-range"
                    >
                      Clear
                    </Button>
                  )}
                </div>
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>

      {/* Custom Range Results */}
      {useCustomRange && customRangeData && (
        <Card className="border-blue-200 bg-blue-500/10/50">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-blue-700">
              <CalendarRange size={20} />
              Custom Range Results
              <span className="text-sm font-normal text-blue-500 ml-2">
                ({customRangeData.months_count} month{customRangeData.months_count !== 1 ? 's' : ''})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="p-4 bg-card border border-border rounded-xl">
                <p className="text-sm text-muted-foreground mb-1">Billable %</p>
                <p className="text-3xl font-bold text-blue-500">{customRangeData.billable_percent}%</p>
                <p className="text-xs text-muted-foreground mt-1">vs {benchmarks.billable_target}% target</p>
              </div>
              <div className="p-4 bg-card border border-border rounded-xl">
                <p className="text-sm text-muted-foreground mb-1">Realization %</p>
                <p className="text-3xl font-bold text-emerald-500">{customRangeData.realization_percent}%</p>
                <p className="text-xs text-muted-foreground mt-1">vs {benchmarks.realization_target}% target</p>
              </div>
              <div className="p-4 bg-card border border-border rounded-xl">
                <p className="text-sm text-muted-foreground mb-1">Bill to Pay %</p>
                <p className="text-3xl font-bold text-amber-500">{customRangeData.bill_to_pay_percent}%</p>
                <p className="text-xs text-muted-foreground mt-1">vs {benchmarks.bill_to_pay_target}% target</p>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4 pt-4 border-t border-border">
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Billable Hours</p>
                <p className="font-semibold text-foreground">{customRangeData.billable_hours?.toFixed(0)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Total Hours</p>
                <p className="font-semibold text-foreground">{customRangeData.total_hours?.toFixed(0)}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Billed Amount</p>
                <p className="font-semibold text-foreground">${customRangeData.billed_amount?.toLocaleString()}</p>
              </div>
              <div className="text-center">
                <p className="text-xs text-muted-foreground">Realized Amount</p>
                <p className="font-semibold text-foreground">${customRangeData.realized_amount?.toLocaleString()}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Main KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <KPICard
          title="Billable %"
          value={useCustomRange && customRangeData ? parseFloat(customRangeData.billable_percent) : (currentYear.billable_percent || 0)}
          benchmark={benchmarks.billable_target}
          previousValue={useCustomRange ? null : previousYear.billable_percent}
          tooltip="Your billable hours divided by total working hours (excluding PLT & Holiday). Shows how much of your time is spent on client work."
        />
        <KPICard
          title="Client Realization"
          value={useCustomRange && customRangeData ? parseFloat(customRangeData.realization_percent) : (currentYear.realization_percent || 0)}
          benchmark={benchmarks.realization_target}
          previousValue={useCustomRange ? null : previousYear.realization_percent}
          tooltip="The percentage of your produced (work done) that was actually billed to the client. Billed amount ÷ Produced amount."
        />
        <KPICard
          title="Bill to Pay %"
          value={useCustomRange && customRangeData ? parseFloat(customRangeData.bill_to_pay_percent) : (currentYear.bill_to_pay_percent || 0)}
          benchmark={benchmarks.bill_to_pay_target}
          previousValue={useCustomRange ? null : previousYear.bill_to_pay_percent}
          tooltip="Your produced (billed) amount divided by your total compensation. Shows how much revenue you generate relative to your pay."
        />
      </div>

      {/* Comparison vs Firm Average */}
      {firmAverages && firmAverages.billable_percent !== undefined && (
        <Card className="border-purple-500/20 bg-purple-500/5">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-foreground">
              <Users size={20} className="text-purple-500" />
              Your Stats vs Firm Average
              <span className="ml-2 text-sm font-normal text-purple-400">
                ({firmAverages.comparison_group === "partners" ? "vs other Partners" : "vs other Employees"})
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Billable % comparison */}
              <div className="p-4 bg-card border border-border rounded-xl">
                <p className="text-sm text-muted-foreground mb-1">Billable %</p>
                <div className="flex items-baseline justify-between">
                  <p className="text-2xl font-bold text-foreground">
                    {(useCustomRange && customRangeData ? parseFloat(customRangeData.billable_percent) : (currentYear.billable_percent || 0)).toFixed(1)}%
                  </p>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Firm Avg</p>
                    <p className="text-sm font-semibold text-purple-400">{firmAverages.billable_percent?.toFixed(1)}%</p>
                  </div>
                </div>
                {(() => {
                  const myVal = useCustomRange && customRangeData ? parseFloat(customRangeData.billable_percent) : (currentYear.billable_percent || 0);
                  const firmVal = firmAverages.billable_percent || 0;
                  const diff = myVal - firmVal;
                  if (firmVal === 0) return null;
                  return (
                    <div className={`mt-2 flex items-center gap-1 ${diff >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                      {diff >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                      <span className="text-xs font-medium">
                        {diff >= 0 ? '+' : ''}{diff.toFixed(1)}% vs firm
                      </span>
                    </div>
                  );
                })()}
              </div>

              {/* Realization % comparison */}
              <div className="p-4 bg-card border border-border rounded-xl">
                <p className="text-sm text-muted-foreground mb-1">Realization %</p>
                <div className="flex items-baseline justify-between">
                  <p className="text-2xl font-bold text-foreground">
                    {(useCustomRange && customRangeData ? parseFloat(customRangeData.realization_percent) : (currentYear.realization_percent || 0)).toFixed(1)}%
                  </p>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Firm Avg</p>
                    <p className="text-sm font-semibold text-purple-400">{firmAverages.realization_percent?.toFixed(1)}%</p>
                  </div>
                </div>
                {(() => {
                  const myVal = useCustomRange && customRangeData ? parseFloat(customRangeData.realization_percent) : (currentYear.realization_percent || 0);
                  const firmVal = firmAverages.realization_percent || 0;
                  const diff = myVal - firmVal;
                  if (firmVal === 0) return null;
                  return (
                    <div className={`mt-2 flex items-center gap-1 ${diff >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                      {diff >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                      <span className="text-xs font-medium">
                        {diff >= 0 ? '+' : ''}{diff.toFixed(1)}% vs firm
                      </span>
                    </div>
                  );
                })()}
              </div>

              {/* Bill to Pay % comparison */}
              <div className="p-4 bg-card border border-border rounded-xl">
                <p className="text-sm text-muted-foreground mb-1">Bill to Pay %</p>
                <div className="flex items-baseline justify-between">
                  <p className="text-2xl font-bold text-foreground">
                    {(useCustomRange && customRangeData ? parseFloat(customRangeData.bill_to_pay_percent) : (currentYear.bill_to_pay_percent || 0)).toFixed(1)}%
                  </p>
                  <div className="text-right">
                    <p className="text-xs text-muted-foreground">Firm Avg</p>
                    <p className="text-sm font-semibold text-purple-400">{firmAverages.bill_to_pay_percent?.toFixed(1)}%</p>
                  </div>
                </div>
                {(() => {
                  const myVal = useCustomRange && customRangeData ? parseFloat(customRangeData.bill_to_pay_percent) : (currentYear.bill_to_pay_percent || 0);
                  const firmVal = firmAverages.bill_to_pay_percent || 0;
                  const diff = myVal - firmVal;
                  if (firmVal === 0) return null;
                  return (
                    <div className={`mt-2 flex items-center gap-1 ${diff >= 0 ? 'text-emerald-500' : 'text-rose-500'}`}>
                      {diff >= 0 ? <TrendingUp size={14} /> : <TrendingDown size={14} />}
                      <span className="text-xs font-medium">
                        {diff >= 0 ? '+' : ''}{diff.toFixed(1)}% vs firm
                      </span>
                    </div>
                  );
                })()}
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Secondary stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-label">Total Hours</p>
                <p className="stat-value text-foreground mt-1">
                  {currentYear.total_hours?.toFixed(0) || 0}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center">
                <Clock size={20} className="text-blue-500" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Previous: {previousYear.total_hours?.toFixed(0) || 0} hrs
            </p>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-label">Billable Hours</p>
                <p className="stat-value text-foreground mt-1">
                  {currentYear.total_billable_hours?.toFixed(0) || 0}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
                <TrendingUp size={20} className="text-emerald-500" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Previous: {previousYear.total_billable_hours?.toFixed(0) || 0} hrs
            </p>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="stat-label">Total Billed</p>
                <p className="stat-value text-foreground mt-1">
                  ${(currentYear.total_realized || 0).toLocaleString()}
                </p>
              </div>
              <div className="w-10 h-10 rounded-lg bg-purple-500/10 flex items-center justify-center">
                <DollarSign size={20} className="text-purple-500" />
              </div>
            </div>
            <p className="text-xs text-muted-foreground mt-3">
              Previous: ${(previousYear.total_realized || 0).toLocaleString()}
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Monthly trend chart */}
      {monthlyStats.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp size={20} className="text-blue-500" />
              Monthly Performance Trend
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-80" data-testid="monthly-chart">
              <ResponsiveContainer width="100%" height="100%">
                <LineChart data={monthlyStats} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#475569" />
                  <XAxis dataKey="month" tick={{ fill: "#94a3b8", fontSize: 12 }} />
                  <YAxis tick={{ fill: "#94a3b8", fontSize: 12 }} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#fff",
                      border: "1px solid #e2e8f0",
                      borderRadius: "8px",
                    }}
                    formatter={(value, name) => {
                      if (name === "Bill to Pay %") {
                        return [`${(value * 10).toFixed(2)}%`, "Bill to Pay %"];
                      }
                      return [`${value.toFixed(2)}%`, name];
                    }}
                  />
                  <Legend />
                  <Line
                    type="monotone"
                    dataKey="Billable %"
                    stroke="#2563eb"
                    strokeWidth={2}
                    dot={{ fill: "#2563eb", strokeWidth: 2 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="Realization %"
                    stroke="#10b981"
                    strokeWidth={2}
                    dot={{ fill: "#10b981", strokeWidth: 2 }}
                  />
                  <Line
                    type="monotone"
                    dataKey="Bill to Pay %"
                    stroke="#f59e0b"
                    strokeWidth={2}
                    dot={{ fill: "#f59e0b", strokeWidth: 2 }}
                  />
                </LineChart>
              </ResponsiveContainer>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              * Bill to Pay % is scaled down by 10x for chart readability
            </p>
          </CardContent>
        </Card>
      )}

      {/* Year comparison */}
      <Card>
        <CardHeader>
          <CardTitle>Year-over-Year Comparison</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-4 bg-muted rounded-xl">
              <p className="stat-label mb-2">Billable %</p>
              <div className="flex items-end gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">{ytdData?.current_year_value}</p>
                  <p className="text-2xl font-bold text-foreground">
                    {currentYear.billable_percent?.toFixed(1)}%
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{ytdData?.previous_year_value}</p>
                  <p className="text-lg text-muted-foreground">
                    {previousYear.billable_percent?.toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-muted rounded-xl">
              <p className="stat-label mb-2">Realization</p>
              <div className="flex items-end gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">{ytdData?.current_year_value}</p>
                  <p className="text-2xl font-bold text-foreground">
                    {currentYear.realization_percent?.toFixed(1)}%
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{ytdData?.previous_year_value}</p>
                  <p className="text-lg text-muted-foreground">
                    {previousYear.realization_percent?.toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>

            <div className="p-4 bg-muted rounded-xl">
              <p className="stat-label mb-2">Bill to Pay</p>
              <div className="flex items-end gap-4">
                <div>
                  <p className="text-xs text-muted-foreground">{ytdData?.current_year_value}</p>
                  <p className="text-2xl font-bold text-foreground">
                    {currentYear.bill_to_pay_percent?.toFixed(1)}%
                  </p>
                </div>
                <div>
                  <p className="text-xs text-muted-foreground">{ytdData?.previous_year_value}</p>
                  <p className="text-lg text-muted-foreground">
                    {previousYear.bill_to_pay_percent?.toFixed(1)}%
                  </p>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Benchmark targets info */}
      <Card>
        <CardHeader>
          <CardTitle>Firm Benchmark Targets</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="flex items-center gap-3 p-3 bg-blue-500/10 rounded-lg">
              <div className="w-2 h-2 rounded-full bg-blue-600" />
              <div>
                <p className="text-sm font-medium text-foreground">Billable %</p>
                <p className="text-lg font-bold text-blue-500">
                  {benchmarks.billable_target}%
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-emerald-500/10 rounded-lg">
              <div className="w-2 h-2 rounded-full bg-emerald-600" />
              <div>
                <p className="text-sm font-medium text-foreground">Realization</p>
                <p className="text-lg font-bold text-emerald-500">
                  {benchmarks.realization_target}%
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3 p-3 bg-amber-500/10 rounded-lg">
              <div className="w-2 h-2 rounded-full bg-amber-600" />
              <div>
                <p className="text-sm font-medium text-foreground">Bill to Pay</p>
                <p className="text-lg font-bold text-amber-500">
                  {benchmarks.bill_to_pay_target}%
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmployeeDashboard;
